import type {LabelPickerLabel} from '@github-ui/item-picker-label'
import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  updateLabelsForLabelableRelayMutation,
  updateLabelsForLabelableRelayMutation$data,
} from './__generated__/updateLabelsForLabelableRelayMutation.graphql'

function projectLabelFields(labels: readonly LabelPickerLabel[]) {
  return labels.map(label => ({
    name: label.name,
    nameHtml: label.nameHTML,
    color: label.color,
    ...(label.description == null ? {} : {description: label.description}),
  }))
}

/**
 * Computes the diff between initial and selected labels
 */
export function computeLabelDiff(
  initialLabels: LabelPickerLabel[],
  selectedLabels: LabelPickerLabel[],
): {labelsToAdd: LabelPickerLabel[]; labelsToRemove: LabelPickerLabel[]} {
  const initialIds = new Set(initialLabels.map(l => l.id))
  const selectedIds = new Set(selectedLabels.map(l => l.id))

  const labelsToAdd = selectedLabels.filter(label => !initialIds.has(label.id))
  const labelsToRemove = initialLabels.filter(label => !selectedIds.has(label.id))

  return {labelsToAdd, labelsToRemove}
}

/**
 * Updates labels on a labelable (Issue or PullRequest) using diff-based approach.
 * This mutation is safer for concurrent edits as it only adds/removes specific labels
 * rather than replacing the entire label set.
 */
export function commitUpdateLabelsForLabelableMutation({
  environment,
  input: {labelableId, labelsToAdd, labelsToRemove, labelableTypeName},
  clientMutationId,
  onError,
  onCompleted,
  expectedFinalLabels,
}: {
  environment: Environment
  input: {
    labelableId: string
    labelsToAdd: LabelPickerLabel[]
    labelsToRemove: LabelPickerLabel[]
    labelableTypeName: 'Issue' | 'PullRequest'
  }
  clientMutationId?: string
  onError?: (error: Error) => void
  onCompleted?: (response: updateLabelsForLabelableRelayMutation$data) => void
  /** Expected final labels after the mutation, used for optimistic response */
  expectedFinalLabels: LabelPickerLabel[]
}) {
  const mutationId = createConnectionMutationId()
  const supportsLabelFieldProjection = labelableTypeName === 'Issue'
  if (supportsLabelFieldProjection) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: labelableId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: {type: 'setFields', id: labelableId, fields: {labels: projectLabelFields(expectedFinalLabels)}},
    })
  }

  const fail = (error: Error) => {
    if (supportsLabelFieldProjection) dispatchConnectionMutation({phase: 'error', mutationId, error})
  }

  let disposable
  try {
    disposable = commitMutation<updateLabelsForLabelableRelayMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateLabelsForLabelableRelayMutation($input: UpdateLabelsForLabelableInput!) @raw_response_type {
          updateLabelsForLabelable(input: $input) {
            labelable {
              labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  node {
                    id
                    color
                    name
                    nameHTML
                    description
                    url
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        input: {
          labelableId,
          addLabelIds: labelsToAdd.map(l => l.id),
          removeLabelIds: labelsToRemove.map(l => l.id),
          clientMutationId,
        },
      },
      optimisticResponse: {
        updateLabelsForLabelable: {
          labelable: {
            __typename: labelableTypeName,
            __isNode: 'true',
            id: labelableId,
            labels: {
              pageInfo: {
                hasNextPage: false,
                endCursor: null,
              },
              edges: expectedFinalLabels.map(a => ({
                cursor: a.id,
                node: {
                  __typename: 'Label',
                  id: a.id,
                  name: a.name,
                  nameHTML: a.nameHTML,
                  color: a.color,
                  url: a.url,
                  description: a.description,
                },
              })),
            },
          },
        },
      },
      onError: error => {
        fail(error)
        onError?.(error)
      },
      onUnsubscribe: () => fail(new Error('Updating issue labels was cancelled')),
      onCompleted: (response, errors) => {
        if (supportsLabelFieldProjection) {
          const labelable = response.updateLabelsForLabelable?.labelable
          const connection = labelable?.labels
          const edges = connection?.edges
          const completeLabels =
            !errors?.length && connection && !connection.pageInfo.hasNextPage && edges
              ? edges.flatMap(edge => (edge?.node ? [edge.node] : []))
              : undefined
          if (!completeLabels || completeLabels.length !== edges?.length) {
            const error = new Error(errors?.[0]?.message ?? 'Failed to update issue labels')
            fail(error)
            onError?.(error)
            return
          }

          dispatchConnectionMutation({
            phase: 'completed',
            mutationId,
            ownerId: labelableId,
            connectionPath: DOCUMENT_FIELDS_PATH,
            operation: {type: 'setFields', id: labelableId, fields: {labels: projectLabelFields(completeLabels)}},
          })
        }
        onCompleted?.(response)
      },
    })
  } catch (error) {
    fail(error instanceof Error ? error : new Error('Failed to update issue labels'))
    throw error
  }

  return disposable
}
